// Fishing. The pond has been there since the first commit and the pack has been
// carrying eight `fish_{dir}_{0,1}` frames the game never drew, so this costs
// almost no new art: a bobber, three ripple frames and a bar in the hud.
//
// The loop is cast → wait → bite → reel. Reeling is a tug of war: the fish
// wanders up and down a bar, holding the use key lifts your catch box against
// gravity, and progress only fills while the box is over the fish.
import {
  BITE_MAX,
  BITE_MIN,
  BITE_WINDOW,
  FISH,
  REEL_TIME,
  type FishDef,
} from "./config.ts";
import type { Dir } from "./player.ts";

export type FishingPhase = "idle" | "waiting" | "bite" | "reeling";

export interface ReelState {
  /** where the fish is on the bar, 0 bottom .. 1 top */
  fish: number;
  /** where the player's catch box is */
  box: number;
  /** how tall the box is, as a fraction of the bar */
  size: number;
  /** 0..1, land it at 1, lose it at 0 */
  progress: number;
  /** seconds left before the line snaps */
  left: number;
  name: string;
}

export interface FishingResult {
  kind: "caught" | "escaped" | "missed";
  fish: FishDef | null;
}

export class Fishing {
  phase: FishingPhase = "idle";
  /** tile-space position of the float */
  bobber = { x: 0, y: 0 };
  dir: Dir = "down";
  reel: ReelState | null = null;

  private timer = 0;
  private fish: FishDef | null = null;
  private fishVel = 0;
  private boxVel = 0;
  private seed = 7;
  private rippleAt = 0;

  get active(): boolean {
    return this.phase !== "idle";
  }

  private rnd(): number {
    this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff;
    return this.seed / 0x7fffffff;
  }

  /** Drop the line onto a water tile. */
  cast(tileX: number, tileY: number, dir: Dir): void {
    this.phase = "waiting";
    this.dir = dir;
    this.bobber = { x: tileX + 0.5, y: tileY + 0.5 };
    this.timer = BITE_MIN + this.rnd() * (BITE_MAX - BITE_MIN);
    this.fish = pickFish(this.rnd());
    this.reel = null;
    this.rippleAt = 0;
  }

  cancel(): void {
    this.phase = "idle";
    this.reel = null;
    this.fish = null;
  }

  /** How far the float has bobbed this frame, in tiles. */
  bobOffset(time: number): number {
    if (this.phase === "bite") return Math.sin(time * 26) * 0.09 - 0.06;
    return Math.sin(time * 3) * 0.035;
  }

  /** True on the frames a ripple should be emitted. */
  wantsRipple(time: number): boolean {
    if (this.phase === "idle") return false;
    const every = this.phase === "bite" ? 0.16 : 0.9;
    if (time - this.rippleAt < every) return false;
    this.rippleAt = time;
    return true;
  }

  /**
   * @param hold true while the use key is down
   * @param strike true on the frame the use key went down
   */
  update(dt: number, hold: boolean, strike: boolean): FishingResult | null {
    switch (this.phase) {
      case "idle":
        return null;

      case "waiting": {
        this.timer -= dt;
        if (strike) {
          this.phase = "idle";
          return { kind: "missed", fish: null };
        }
        if (this.timer <= 0) {
          this.phase = "bite";
          this.timer = BITE_WINDOW;
        }
        return null;
      }

      case "bite": {
        this.timer -= dt;
        if (strike) {
          const f = this.fish!;
          this.phase = "reeling";
          this.reel = {
            fish: 0.5,
            box: 0.35,
            size: 0.3 - f.difficulty * 0.16,
            progress: 0.35,
            left: REEL_TIME,
            name: f.name,
          };
          this.fishVel = 0;
          this.boxVel = 0;
          return null;
        }
        if (this.timer <= 0) {
          this.phase = "idle";
          return { kind: "escaped", fish: this.fish };
        }
        return null;
      }

      case "reeling": {
        const r = this.reel!;
        const f = this.fish!;
        r.left -= dt;

        // the fish drifts, then darts
        this.fishVel += (this.rnd() - 0.5) * (0.9 + f.difficulty * 2.6) * dt * 12;
        this.fishVel *= 0.9;
        r.fish = clamp01(r.fish + this.fishVel * dt);
        if (r.fish <= 0 || r.fish >= 1) this.fishVel *= -0.5;

        // the box falls unless you hold the line
        this.boxVel += (hold ? 1.9 : -1.9) * dt;
        this.boxVel *= 0.86;
        r.box = clamp01(r.box + this.boxVel * dt);
        if (r.box <= 0 || r.box >= 1) this.boxVel = 0;

        const inside = Math.abs(r.box - r.fish) < r.size / 2;
        r.progress = clamp01(r.progress + (inside ? 0.42 : -0.3) * dt);

        if (r.progress >= 1) {
          this.phase = "idle";
          this.reel = null;
          return { kind: "caught", fish: f };
        }
        if (r.progress <= 0 || r.left <= 0) {
          this.phase = "idle";
          this.reel = null;
          return { kind: "escaped", fish: f };
        }
        return null;
      }
    }
  }
}

function pickFish(roll: number): FishDef {
  const total = FISH.reduce((a, f) => a + f.weight, 0);
  let t = roll * total;
  for (const f of FISH) {
    t -= f.weight;
    if (t <= 0) return f;
  }
  return FISH[0]!;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
